// Docs: FEATURES.md "App structure" (wake screen game). The mini-games on the
// wake screen (components/WakeGame.jsx). Each game turns a bundled deck
// (utils/wakeGameWords.js) into rounds of one prompt and three choices:
//   { key, prompt, cue?, long?, answer, choices: [{ value, label, tone?, shape? }],
//     reveal: { pre, fill, post, tone? }, detail }
// `reveal` is the prompt as shown after answering, with `fill` highlighted.
import {
  WAKE_COLLOCATIONS,
  WAKE_IRREGULAR_VERBS,
  WAKE_MIXED_VERBS,
  WAKE_NOUNS,
  WAKE_PREPOSITIONS,
  WAKE_PRETERITE,
  WAKE_REFLEXIVES,
  WAKE_VERB_CASES,
  WAKE_VERB_PREPOSITIONS,
  WAKE_VERBS,
  endingRule,
  shuffled,
} from './wakeGameWords';

// Same shape per article as everywhere else in the app (GenusShape):
// der ■, die ●, das ▲ — the wake screen used to swap them around, which
// taught the opposite association to the lessons.
export const ARTICLES = [
  { article: 'der', key: '1', shape: 'square' },
  { article: 'die', key: '2', shape: 'circle' },
  { article: 'das', key: '3', shape: 'triangle' },
];

const ARTICLE_CHOICES = ARTICLES.map(({ article, shape }) => ({
  value: article, label: article, tone: article, shape,
}));

// --- Plural -----------------------------------------------------------------

// Umlaut on the last a/o/u/au of the stem: Apfel → Äpfel, Haus → Häus.
const UMLAUT = { a: 'ä', o: 'ö', u: 'ü', A: 'Ä', O: 'Ö', U: 'Ü' };
export function umlauted(noun) {
  const m = noun.match(/^(.*?)(au|Au|[aouAOU])([^aouAOUäöüÄÖÜ]*)$/);
  if (!m) return null;
  const [, head, vowel, tail] = m;
  const swapped = vowel.length === 2 ? `${UMLAUT[vowel[0]]}u` : UMLAUT[vowel];
  return `${head}${swapped}${tail}`;
}

const NO_UMLAUT_SUFFIX = /(ung|ion|tät|heit|keit|schaft|ei|um|ment|chen|lein|ling|ismus)$/;

// Plausible learner plurals: the usual endings on the bare and umlauted stem.
export function pluralCandidates(noun) {
  const stems = [noun];
  const uml = noun.length <= 8 && !/e$/.test(noun) && !NO_UMLAUT_SUFFIX.test(noun) ? umlauted(noun) : null;
  if (uml) stems.push(uml);
  const out = new Set();
  for (const stem of stems) {
    if (/[aiouy]$/.test(stem)) {
      ['', 's', 'n', 'en'].forEach((s) => out.add(stem + s));
    } else if (/e$/.test(stem)) {
      ['', 'n', 's', 'r'].forEach((s) => out.add(stem + s));
    } else if (/(el|er)$/.test(stem)) {
      ['', 'n', 's'].forEach((s) => out.add(stem + s));
    } else if (/n$/.test(stem)) {
      ['', 's', 'e'].forEach((s) => out.add(stem + s));
    } else {
      ['', 'e', 'en', 'er', 's'].forEach((s) => out.add(stem + s));
    }
  }
  return [...out];
}

function pluralRule(article, noun, plural) {
  const suf = noun.match(/(ung|heit|keit|schaft|ion|tät|ei)$/);
  if (suf && plural === `${noun}en`) return `-${suf[1]} → Plural -en`;
  const dim = noun.match(/(chen|lein)$/);
  if (dim && plural === noun) return `-${dim[1]} → Plural = Singular`;
  if (article === 'die' && /e$/.test(noun) && plural === `${noun}n`) return 'die …e → Plural -n';
  if (article !== 'die' && /(el|er|en)$/.test(noun) && (plural === noun || plural === umlauted(noun))) {
    return '-el / -er / -en → meist ohne Endung';
  }
  if (/[aiouy]$/.test(noun) && plural === `${noun}s`) return 'Endung auf Vokal → oft -s';
  return null;
}

function pluralRounds(random) {
  return shuffled(WAKE_NOUNS.filter(([, , plural]) => plural), random).map(([article, noun, plural]) => {
    const wrong = shuffled(pluralCandidates(noun).filter((p) => p !== plural), random).slice(0, 2);
    return {
      key: `${noun}-pl`,
      cue: `${article} ${noun} →`,
      prompt: 'die …?',
      answer: plural,
      choices: shuffled([plural, ...wrong], random).map((p) => ({ value: p, label: p })),
      reveal: { pre: 'die ', fill: plural, post: '' },
      detail: pluralRule(article, noun, plural),
    };
  });
}

// --- der / die / das ----------------------------------------------------------

function articleRounds(random) {
  return shuffled(WAKE_NOUNS, random).map(([article, noun, plural]) => ({
    key: noun,
    prompt: noun,
    answer: article,
    choices: ARTICLE_CHOICES,
    reveal: { pre: '', fill: `${article} `, post: noun, tone: article },
    detail: [plural ? `Plural: die ${plural}` : null, endingRule(noun, article)].filter(Boolean).join(' · ') || null,
  }));
}

// --- Perfekt -------------------------------------------------------------------

function perfektRule(infinitive, aux, participle) {
  if (/ieren$/.test(infinitive)) return '-ieren → Partizip ohne ge-';
  if (/^(be|ver|er|ent|zer|emp|ge)/.test(infinitive) && !participle.startsWith('ge')) {
    return 'be-/ver-/er-/ent- … → ohne ge-';
  }
  if (/^(auf|ein|an|mit|um|aus|zu|ab)/.test(infinitive)) return 'trennbar → ge- in der Mitte';
  return aux === 'ist' ? 'Bewegung / Veränderung → sein' : null;
}

function perfektRounds(random) {
  return shuffled(WAKE_VERBS, random).map(([infinitive, aux, participle, wrong]) => {
    const right = `${aux} ${participle}`;
    const options = [right, `${aux === 'ist' ? 'hat' : 'ist'} ${participle}`, `${aux} ${wrong}`];
    return {
      key: `${infinitive}-pf`,
      cue: 'Perfekt: er/sie …',
      prompt: infinitive,
      answer: right,
      choices: shuffled(options, random).map((o) => ({ value: o, label: o })),
      reveal: { pre: `${infinitive} → `, fill: right, post: '' },
      detail: perfektRule(infinitive, aux, participle),
    };
  });
}

// --- Gap decks: Präposition, Reflexiv, Kasus, Verb + Präposition, Nomen-Verb --

// [phrase with ___, right, [wrong, wrong], rule] → a fill-the-gap round.
const gapRounds = (deck) => (random) => shuffled(deck, random).map(([phrase, right, wrong, rule]) => {
  const [pre, post] = phrase.split('___');
  return {
    key: phrase,
    prompt: phrase.replace('___', '…'),
    long: phrase.length > 18,
    answer: right,
    choices: shuffled([right, ...wrong], random).map((a) => ({ value: a, label: a })),
    reveal: { pre, fill: right, post },
    // A rule that only repeats the filled-in phrase adds nothing.
    detail: rule === `${pre}${right}${post}` ? null : rule,
  };
});

// --- Präteritum ----------------------------------------------------------------

function preteriteRule(infinitive, form) {
  if (WAKE_IRREGULAR_VERBS.has(infinitive)) return 'unregelmäßig — auswendig lernen';
  if (WAKE_MIXED_VERBS.has(infinitive)) return 'gemischt: Vokalwechsel + -te';
  if (form === `${infinitive.replace(/e?n$/, '')}te`) return 'regelmäßig: Stamm + -te';
  return 'stark: Vokalwechsel, keine Endung';
}

function preteriteRounds(random) {
  return shuffled(WAKE_PRETERITE, random).map(([infinitive, right, wrong]) => ({
    key: `${infinitive}-prt`,
    cue: 'Präteritum: er/sie …',
    prompt: infinitive,
    answer: right,
    choices: shuffled([right, ...wrong], random).map((f) => ({ value: f, label: f })),
    reveal: { pre: `${infinitive} → er `, fill: right, post: '' },
    detail: preteriteRule(infinitive, right),
  }));
}

// `storageKey` keeps a separate top list per game; der/die/das keeps the
// original key so existing records carry over.
export const WAKE_GAMES = [
  { id: 'artikel', tab: 'der/die/das', title: 'der, die oder das?', storageKey: 'gt-wake-top-streaks', rounds: articleRounds },
  { id: 'plural', tab: 'Plural', title: 'Wie heißt der Plural?', storageKey: 'gt-wake-top-streaks:plural', rounds: pluralRounds },
  { id: 'perfekt', tab: 'Perfekt', title: 'hat oder ist?', storageKey: 'gt-wake-top-streaks:perfekt', rounds: perfektRounds },
  { id: 'praeteritum', tab: 'Präteritum', title: 'Wie im Präteritum?', storageKey: 'gt-wake-top-streaks:praeteritum', rounds: preteriteRounds },
  { id: 'kasus', tab: 'Präposition', title: 'Welcher Fall?', storageKey: 'gt-wake-top-streaks:kasus', rounds: gapRounds(WAKE_PREPOSITIONS) },
  { id: 'verbkasus', tab: 'Kasus', title: 'Dativ oder Akkusativ?', storageKey: 'gt-wake-top-streaks:verbkasus', rounds: gapRounds(WAKE_VERB_CASES) },
  { id: 'reflexiv', tab: 'Reflexiv', title: 'mich oder mir?', storageKey: 'gt-wake-top-streaks:reflexiv', rounds: gapRounds(WAKE_REFLEXIVES) },
  { id: 'verbpraep', tab: 'Verb + Präp.', title: 'Welche Präposition?', storageKey: 'gt-wake-top-streaks:verbpraep', rounds: gapRounds(WAKE_VERB_PREPOSITIONS) },
  { id: 'nvv', tab: 'Nomen-Verb', title: 'Welches Verb passt?', storageKey: 'gt-wake-top-streaks:nvv', rounds: gapRounds(WAKE_COLLOCATIONS) },
];

// Random game for a new wake screen, never the one played last time.
export function pickGame(lastId, random = Math.random) {
  const pool = WAKE_GAMES.filter((g) => g.id !== lastId);
  return pool[Math.floor(random() * pool.length)];
}

export const gameById = (id) => WAKE_GAMES.find((g) => g.id === id) || WAKE_GAMES[0];
